export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function getApiErrorMessage(error, fallback = "Failed to save") {
  const detail = error?.response?.data?.detail;
  return detail != null ? formatApiError(detail) : fallback;
}

export function inr(n) {
  const num = Number(n || 0);
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Formats a date string, timestamp, or Date object into DD/MM/YYYY.
 * Guaranteed timezone-safe: prevents YYYY-MM-DD from shifting backward or forward.
 * Example: "2026-10-01" -> "01/10/2026"
 */
export function formatDate(val) {
  if (!val) return "—";
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return "—";
    const datePart = trimmed.split(/[T ]/)[0];
    const match = datePart.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    if (match) {
      const [, y, m, d] = match;
      return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
    }
  }
  const dt = val instanceof Date ? val : new Date(val);
  if (isNaN(dt.getTime())) return String(val);
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Formats a date and time string into DD/MM/YYYY, hh:mm A.
 */
export function formatDateTime(val) {
  if (!val) return "—";
  const dt = val instanceof Date ? val : new Date(val);
  if (isNaN(dt.getTime())) return formatDate(val);
  const dateStr = formatDate(dt);
  const timeStr = dt.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateStr}, ${timeStr}`;
}

/**
 * Cleanly pluralizes or singularizes pharmacy unit labels.
 * Handles singular, plural, and irregularly inflected words.
 */
export function pluralizeUnit(count, unit) {
  if (!unit) return "";
  const trimmed = String(unit).trim();
  if (!trimmed) return "";

  // Normalize plural if already ending with 's' or similar
  let singular = trimmed;
  const lowerTrimmed = trimmed.toLowerCase();
  if (lowerTrimmed.endsWith("boxes")) {
    singular = trimmed.slice(0, -2);
  } else if (lowerTrimmed.endsWith("ies") && !lowerTrimmed.endsWith("series")) {
    singular = `${trimmed.slice(0, -3)}y`;
  } else if (
    lowerTrimmed.endsWith("es") &&
    (lowerTrimmed.endsWith("shes") || lowerTrimmed.endsWith("ches") || lowerTrimmed.endsWith("xes"))
  ) {
    singular = trimmed.slice(0, -2);
  } else if (
    lowerTrimmed.endsWith("s") &&
    !lowerTrimmed.endsWith("ss") &&
    !lowerTrimmed.endsWith("us") &&
    !lowerTrimmed.endsWith("is")
  ) {
    singular = trimmed.slice(0, -1);
  }

  if (Math.abs(count) === 1) {
    return singular;
  }

  const lowerSingular = singular.toLowerCase();
  if (
    lowerSingular.endsWith("s") ||
    lowerSingular.endsWith("sh") ||
    lowerSingular.endsWith("ch") ||
    lowerSingular.endsWith("x") ||
    lowerSingular.endsWith("z")
  ) {
    return `${singular}es`;
  }
  if (lowerSingular.endsWith("y") && !/[aeiou]y$/i.test(lowerSingular)) {
    return `${singular.slice(0, -1)}ies`;
  }
  return `${singular}s`;
}

/**
 * Shared helper to format stock quantity with base and pack context.
 * Display only - does not affect internal calculations or DB storage.
 *
 * Rules:
 * - If units_per_pack = 1 or packing_unit == base_unit: show only primary base unit (no redundant pack-equivalent).
 * - Exact multiples (e.g. 450 Tablets with 10/pack) -> "45 Strips"
 * - Non-exact quantities (e.g. 457 Tablets with 10/pack) -> "45 Strips + 7 Tablets"
 * - Clean singular/plural (e.g. 1 Tablet, 2 Tablets, 1 Strip, 2 Strips)
 */
export function formatStockDisplay(stock, med) {
  const baseQty = Number(stock || 0);
  const baseUnit = med?.base_unit || med?.unit || "Unit";
  const packingUnit = med?.packing_unit || med?.unit || baseUnit;
  const unitsPerPack = Math.max(1, Number(med?.units_per_pack || 1));

  const primary = `${baseQty} ${pluralizeUnit(baseQty, baseUnit)}`;

  const isSameUnit = packingUnit.trim().toLowerCase() === baseUnit.trim().toLowerCase();

  // If units_per_pack = 1 or packing_unit == base_unit, show only one meaningful stock unit
  if (unitsPerPack <= 1 || isSameUnit) {
    return {
      primary,
      secondary: null,
      fullText: primary,
    };
  }

  const absQty = Math.abs(baseQty);
  const sign = baseQty < 0 ? "-" : "";
  const packCount = Math.floor(absQty / unitsPerPack);
  const looseCount = absQty % unitsPerPack;

  let secondary = null;
  if (absQty >= unitsPerPack) {
    const packStr = `${sign}${packCount} ${pluralizeUnit(packCount, packingUnit)}`;
    if (looseCount === 0) {
      secondary = packStr;
    } else {
      secondary = `${packStr} + ${looseCount} ${pluralizeUnit(looseCount, baseUnit)}`;
    }
  } else if (baseQty === 0) {
    secondary = `0 ${pluralizeUnit(0, packingUnit)}`;
  }

  const fullText = secondary ? `${primary} · ${secondary}` : primary;

  return {
    primary,
    secondary,
    fullText,
  };
}
